import apiChat from 'apiChat';
import apiAI from 'apiAI';

const PATH_AI = '/Ai';
const PATH_BRIEFING = '/SolicitacaoBriefing';

/**
 * Último briefing em rascunho com texto e arquivos (Chat API).
 * Fallback: API AI retorna só o texto (sem anexos).
 */
const GET_ULTIMO_BRIEFING_POR_USUARIO = async (usuarioId) => {
  try {
    const { data } = await apiChat.get(`${PATH_BRIEFING}/GetBriefingUsuario`, {
      params: { usuarioId },
    });
    return data;
  } catch (e) {
    const status = e?.response?.status;
    if (status && status !== 404) throw e;
    const { data } = await apiAI.get(
      `${PATH_AI}/GetBriefing?usuarioId=${usuarioId}`
    );
    if (typeof data === 'string') {
      return { value: { briefing: data, arquivos: [] } };
    }
    return data;
  }
};

/**
 * Envia briefing e anexos (SendBriefingAsync: [FromForm] briefing, usuarioId, arquivos).
 */
const POST_ENVIAR_BRIEFING = async (formData) => {
  const { data } = await apiChat.post(`${PATH_BRIEFING}/Send`, formData, {
    headers: {
      accept: 'text/plain',
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

const PUT_ENVIAR_BRIEFING = async (formData) => {
  const { data } = await apiChat.put(`${PATH_BRIEFING}/Update`, formData, {
    headers: {
      accept: 'text/plain',
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

/** Resposta muitas vezes vem como string JSON quando o servidor usa text/plain. */
const parseProcessAiResponseData = (data) => {
  if (data == null || typeof data === 'object') return data;
  if (typeof data === 'string') {
    const t = data.trim();
    if (
      (t.startsWith('{') && t.endsWith('}')) ||
      (t.startsWith('[') && t.endsWith(']'))
    ) {
      try {
        return JSON.parse(t);
      } catch {
        return data;
      }
    }
  }
  return data;
};

const PROCESS_AI_EXTERNA = async (usuarioId) => {
  const { data } = await apiChat.get(
    `${PATH_BRIEFING}/ProcessAi?usuarioId=${usuarioId}`,
    {
      headers: {
        Accept: 'application/json, text/plain;q=0.9',
      },
    }
  );
  return parseProcessAiResponseData(data);
};

const CONSULTAR_BRIEFING_AI_EXTERNA = async (usuarioId) => {
  const { data } = await apiAI.get(
    `${PATH_AI}/GetBriefing?usuarioId=${usuarioId}`
  );
  return parseProcessAiResponseData(data);
};

/** O corpo da lambda pode vir como texto puro ou JSON com texto/text/value. */
const parseTranscricaoTexto = (raw) => {
  if (raw == null) return '';
  if (typeof raw !== 'string') return String(raw);
  const t = raw.trim();
  if (
    (t.startsWith('{') && t.endsWith('}')) ||
    (t.startsWith('[') && t.endsWith(']'))
  ) {
    try {
      const parsed = JSON.parse(t);
      if (parsed != null && typeof parsed === 'object') {
        return (
          parsed.texto ??
          parsed.Texto ??
          parsed.text ??
          parsed.Text ??
          parsed.value ??
          parsed.Value ??
          raw
        );
      }
    } catch {
      return raw;
    }
  }
  return raw;
};

/**
 * Envia o áudio gravado para SolicitacaoBriefing/TranscreverAi, que encaminha
 * o áudio (multipart, assinado via SigV4) para a lambda de transcrição.
 */
const TRANSCREVER_AUDIO = async (audioBlob, usuarioId) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'gravacao.webm');
  formData.append('usuarioId', String(usuarioId ?? ''));

  const { data } = await apiChat.post(
    `${PATH_BRIEFING}/TranscreverAi`,
    formData,
    {
      headers: {
        accept: 'text/plain',
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  const erro = data?.error ?? data?.Error;
  if (erro) {
    throw new Error(erro);
  }

  return parseTranscricaoTexto(data?.message ?? data?.Message ?? '');
};

export {
  GET_ULTIMO_BRIEFING_POR_USUARIO,
  POST_ENVIAR_BRIEFING,
  PUT_ENVIAR_BRIEFING,
  PROCESS_AI_EXTERNA,
  CONSULTAR_BRIEFING_AI_EXTERNA,
  TRANSCREVER_AUDIO,
};
